import type { WhiteboardInteractionTool } from "@klass-share/whiteboard-core";
import { useEffect, useState } from "react";

import { useNoticeAutoDismiss } from "../notice-dismiss.js";
import { strings } from "../strings.js";

export type WhiteboardUiTool = WhiteboardInteractionTool | "text";

export interface WhiteboardPageListItem {
  readonly pageId: string;
}

export interface WhiteboardPagePosition {
  readonly index: number;
  readonly count: number;
}

export interface WhiteboardGateContext {
  readonly boardLocked: boolean;
  readonly drawingVerified: boolean;
  readonly hostTimeAnchored: boolean;
}

export type WhiteboardGateReason = "locked" | "unverified" | "syncing";

export interface WhiteboardGateChipPresentation {
  readonly reason: WhiteboardGateReason;
  readonly state: "open" | "closing";
}

export function whiteboardPagePosition(
  pages: readonly WhiteboardPageListItem[],
  activePage: string | undefined,
): WhiteboardPagePosition | undefined {
  if (activePage === undefined) return undefined;
  const index = pages.findIndex((page) => page.pageId === activePage);
  return index < 0 ? undefined : { index: index + 1, count: pages.length };
}

export function whiteboardGateReasons(
  context: WhiteboardGateContext,
): readonly WhiteboardGateReason[] {
  return [
    ...(context.boardLocked ? (["locked"] as const) : []),
    ...(!context.drawingVerified ? (["unverified"] as const) : []),
    ...(!context.hostTimeAnchored ? (["syncing"] as const) : []),
  ];
}

export function whiteboardGateChipsAfter(
  current: readonly WhiteboardGateChipPresentation[],
  reasons: readonly WhiteboardGateReason[],
): readonly WhiteboardGateChipPresentation[] {
  const desired = new Set(reasons);
  return [
    ...reasons.map((reason) => ({ reason, state: "open" as const })),
    ...current
      .filter((chip) => !desired.has(chip.reason))
      .map((chip) => ({ ...chip, state: "closing" as const })),
  ];
}

export interface WbToolDockProps {
  readonly tool: WhiteboardUiTool;
  readonly color: string;
  readonly width: number;
  readonly editable: boolean;
  readonly canUndo: boolean;
  readonly canRedo: boolean;
  readonly onTool: (tool: WhiteboardUiTool) => void;
  readonly onColor: (color: string) => void;
  readonly onWidth: (width: number) => void;
  readonly onUndo: () => void;
  readonly onRedo: () => void;
}

export function WbToolDock({
  tool,
  color,
  width,
  editable,
  canUndo,
  canRedo,
  onTool,
  onColor,
  onWidth,
  onUndo,
  onRedo,
}: WbToolDockProps): React.JSX.Element {
  const [expanded, setExpanded] = useState(true);
  const eraserSelected = tool === "erase-whole";
  return (
    <div
      className="wb-tool-dock"
      data-wb-dock-expanded={expanded}
      role="group"
      aria-label={strings.whiteboard.toolsAria}
    >
      <button
        type="button"
        className="wb-icon-button wb-dock-toggle"
        data-wb-dock-toggle
        aria-expanded={expanded}
        aria-label={strings.whiteboard.toolsToggleAria}
        onClick={() => setExpanded((value) => !value)}
      >
        <ChevronDownIcon />
      </button>
      <div
        className="wb-tool-buttons"
        role="group"
        aria-label={strings.whiteboard.drawingToolsAria}
      >
        <WbToolButton
          icon={<PenIcon />}
          label={strings.whiteboard.pen}
          tool="pen"
          selected={tool === "pen"}
          disabled={!editable}
          onSelect={onTool}
        />
        <WbToolButton
          icon={<HighlighterIcon />}
          label={strings.whiteboard.highlighter}
          tool="highlighter"
          selected={tool === "highlighter"}
          disabled={!editable}
          onSelect={onTool}
        />
        <WbToolButton
          icon={<TextIcon />}
          label={strings.whiteboard.text}
          tool="text"
          selected={tool === "text"}
          disabled={!editable}
          onSelect={onTool}
        />
        <button
          type="button"
          className="wb-tool-button"
          data-wb-tool="eraser"
          aria-pressed={eraserSelected}
          disabled={!editable}
          onClick={() => onTool("erase-whole")}
        >
          <span className="wb-tool-content" data-wb-tool-content>
            <EraserIcon />
            <span>{strings.whiteboard.eraser}</span>
          </span>
        </button>
      </div>

      <label className="wb-color-control">
        <span>{strings.whiteboard.color}</span>
        <input
          type="color"
          data-wb-color
          value={color}
          disabled={!editable || eraserSelected}
          onChange={(event) => onColor(event.target.value)}
        />
      </label>

      <label className="wb-width-control">
        <span>{strings.whiteboard.width(width)}</span>
        <input
          type="range"
          data-wb-width
          min="1"
          max="24"
          value={width}
          disabled={!editable || tool === "erase-whole"}
          onChange={(event) => onWidth(Number(event.target.value))}
        />
      </label>

      <div
        className="wb-history-buttons"
        role="group"
        aria-label={strings.whiteboard.historyAria}
      >
        <button
          type="button"
          className="wb-icon-button"
          data-wb-action="undo"
          aria-label={strings.whiteboard.undoAria}
          disabled={!editable || !canUndo}
          onClick={onUndo}
        >
          <UndoIcon />
        </button>
        <button
          type="button"
          className="wb-icon-button"
          data-wb-action="redo"
          aria-label={strings.whiteboard.redoAria}
          disabled={!editable || !canRedo}
          onClick={onRedo}
        >
          <RedoIcon />
        </button>
      </div>
    </div>
  );
}

function WbToolButton({
  icon,
  label,
  tool,
  selected,
  disabled,
  onSelect,
}: {
  readonly icon: React.JSX.Element;
  readonly label: string;
  readonly tool: WhiteboardUiTool;
  readonly selected: boolean;
  readonly disabled: boolean;
  readonly onSelect: (tool: WhiteboardUiTool) => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className="wb-tool-button"
      data-wb-tool={tool}
      aria-pressed={selected}
      disabled={disabled}
      onClick={() => onSelect(tool)}
    >
      <span className="wb-tool-content" data-wb-tool-content>
        {icon}
        <span>{label}</span>
      </span>
    </button>
  );
}

export function WbStatus({
  page,
  connected,
  persistenceSynced,
}: {
  readonly page: WhiteboardPagePosition | undefined;
  readonly connected: boolean;
  readonly persistenceSynced: boolean;
}): React.JSX.Element {
  return (
    <div className="wb-status" data-wb-status role="status">
      <span
        className="wb-page-status"
        data-wb-page-status
        key={page?.index ?? "preparing"}
      >
        {page === undefined
          ? strings.whiteboard.pagePreparing
          : strings.whiteboard.pagePosition(page.index, page.count)}
      </span>
      <span
        className={`wb-sync-status ${connected ? "wb-sync-connected" : "wb-sync-reconnecting"}`}
        title={
          persistenceSynced
            ? strings.whiteboard.localRecoveryReady
            : strings.whiteboard.localRecoveryPreparing
        }
      >
        <span
          className="wb-sync-indicator"
          data-wb-sync-indicator={connected ? "connected" : "reconnecting"}
          key={connected ? "connected" : "reconnecting"}
        >
          {connected ? (
            <StatusDotIcon />
          ) : (
            <span className="wb-sync-spinner" aria-hidden="true" />
          )}
        </span>
        <span>
          {connected
            ? strings.whiteboard.synchronized
            : strings.whiteboard.reconnecting}
        </span>
        <span className="visually-hidden">
          {persistenceSynced
            ? strings.whiteboard.localRecoveryReady
            : strings.whiteboard.localRecoveryPreparing}
        </span>
      </span>
    </div>
  );
}

export function WbGateChips({
  reasons,
  revealNonce = 0,
}: {
  readonly reasons: readonly WhiteboardGateReason[];
  /** Bump to re-reveal the chips (e.g. tapping a locked board) and restart the beat. */
  readonly revealNonce?: number;
}): React.JSX.Element | null {
  const reasonsKey = reasons.join(",");
  // Notice-style popup: show the gate chips for a beat, then auto-dismiss. A
  // changed reason set — or a bumped reveal nonce — restarts the countdown so a
  // new gate, or a fresh tap on a locked board, always shows the message again.
  const dismissed = useNoticeAutoDismiss(
    reasonsKey === "" ? undefined : `${reasonsKey}#${revealNonce}`,
  );
  const [chips, setChips] = useState<readonly WhiteboardGateChipPresentation[]>(
    () => whiteboardGateChipsAfter([], reasons),
  );
  useEffect(() => {
    setChips((current) => {
      const next = whiteboardGateChipsAfter(current, dismissed ? [] : reasons);
      return sameGateChipPresentation(current, next) ? current : next;
    });
  }, [reasons, dismissed]);
  if (chips.length === 0) return null;
  return (
    <div className="wb-gate-chips" data-wb-gates role="status">
      {chips.map(({ reason, state }) => (
        <span
          className="wb-gate-chip"
          data-wb-gate={reason}
          data-wb-gate-state={state}
          aria-hidden={state === "closing" ? true : undefined}
          key={reason}
          onAnimationEnd={(event) => {
            if (
              event.currentTarget !== event.target ||
              state !== "closing" ||
              (event.animationName !== "wb-gate-chip-exit" &&
                event.animationName !== "wb-gate-chip-exit-reduced")
            )
              return;
            setChips((current) =>
              current.filter((chip) => chip.reason !== reason),
            );
          }}
        >
          {reason === "locked" && <LockIcon />}
          {gateReasonLabel(reason)}
        </span>
      ))}
    </div>
  );
}

function sameGateChipPresentation(
  left: readonly WhiteboardGateChipPresentation[],
  right: readonly WhiteboardGateChipPresentation[],
): boolean {
  return (
    left.length === right.length &&
    left.every(
      (chip, index) =>
        chip.reason === right[index]?.reason &&
        chip.state === right[index]?.state,
    )
  );
}

function gateReasonLabel(reason: WhiteboardGateReason): string {
  switch (reason) {
    case "locked":
      return strings.whiteboard.boardLocked;
    case "unverified":
      return strings.whiteboard.viewOnlyUnverified;
    case "syncing":
      return strings.whiteboard.hostTimeSyncing;
  }
}

function IconFrame({
  viewBox,
  children,
}: {
  readonly viewBox: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <svg
      className="wb-tool-icon"
      viewBox={viewBox}
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

// KlassMate in-house SVG; source: mono/edusync-klassmate-mono-v1/packages/ui/src/icons/custom/PenIcon.tsx. Cross-repo reuse approved by DECISIONS.md 2026-07-15 O9.
export function PenIcon(): React.JSX.Element {
  return (
    <IconFrame viewBox="0 0 20 22">
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.316665 3.26595L2.96667 5.62294C3.17778 5.93234 3.48889 6.11111 3.81667 6.11111L16.1722 6.11111C16.5 6.11111 16.8111 5.93234 17.0222 5.62294L19.6722 3.26595C19.8833 3.00468 20 2.66089 20 2.29648L20 0L0 -1.74846e-06L-2.00164e-07 2.2896C-2.32021e-07 2.65401 0.116668 3.00467 0.327779 3.25907L0.316665 3.26595ZM4.79167 7.33333L15.2083 7.33333C15.7836 7.33333 16.25 7.88054 16.25 8.55556L16.25 9.77778C16.25 10.4528 15.7836 11 15.2083 11L4.79167 11C4.21637 11 3.75 10.4528 3.75 9.77778L3.75 8.55555C3.75 7.88054 4.21637 7.33333 4.79167 7.33333ZM5 12.2222L14.9998 12.2222L15 17.1973C15 19.8484 12.76 22 10.0001 22C7.24016 22 5.00021 19.8484 5.00021 17.1973L5 12.2222Z"
      />
    </IconFrame>
  );
}

// KlassMate in-house SVG; source: mono/edusync-klassmate-mono-v1/packages/ui/src/icons/custom/HighlighterIcon.tsx. Cross-repo reuse approved by DECISIONS.md 2026-07-15 O9.
export function HighlighterIcon(): React.JSX.Element {
  return (
    <IconFrame viewBox="0 0 20 22">
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M0.327778 2.61276L3.05 5.30803C3.57222 5.82508 4.27778 6.11111 5.01667 6.11111L14.9889 6.11111C15.7278 6.11111 16.4333 5.81958 16.9556 5.30803L19.6722 2.61276C19.8833 2.40374 20 2.12871 20 1.83718V0H0V1.83168C0 2.12321 0.116667 2.40374 0.327778 2.60726V2.61276ZM6.25 21.179V13.0426C6.25 12.5896 6.6231 12.2224 7.08333 12.2224H12.9167C13.3769 12.2224 13.75 12.5896 13.75 13.0426V18.4996C13.75 18.8183 13.5625 19.1081 13.2691 19.2429L7.43574 21.9222C6.88325 22.176 6.25 21.779 6.25 21.179ZM15.2083 7.33333L4.79167 7.33333C4.21637 7.33333 3.75 7.88054 3.75 8.55556L3.75 9.77778C3.75 10.4528 4.21637 11 4.79167 11L15.2083 11C15.7836 11 16.25 10.4528 16.25 9.77778V8.55556C16.25 7.88054 15.7836 7.33333 15.2083 7.33333Z"
      />
    </IconFrame>
  );
}

// KlassShare-only text glyph on the KlassMate 20 px icon grid, permitted by DECISIONS.md 2026-07-15 O9.
export function TextIcon(): React.JSX.Element {
  return (
    <IconFrame viewBox="0 0 20 20">
      <path
        fill="currentColor"
        d="M2 2.5C2 1.67157 2.67157 1 3.5 1H16.5C17.3284 1 18 1.67157 18 2.5V5H15.5V3.5H11.5V16.5H14V19H6V16.5H8.5V3.5H4.5V5H2V2.5Z"
      />
    </IconFrame>
  );
}

// KlassMate in-house SVG; source: mono/edusync-klassmate-mono-v1/packages/ui/src/icons/custom/EraserIcon.tsx. Cross-repo reuse approved by DECISIONS.md 2026-07-15 O9.
export function EraserIcon(): React.JSX.Element {
  return (
    <IconFrame viewBox="0 0 16 20">
      <path
        fill="currentColor"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16 0H0V11.7647H16V0ZM2.37333 19.6658L0.253333 17.0068L0.262224 17.0021C0.0933348 16.8279 0 16.5879 0 16.3385V14.1177L16 14.1177V16.3432C16 16.5926 15.9067 16.8279 15.7378 17.0068L13.6178 19.6658C13.4489 19.8776 13.2 20 12.9378 20H3.05333C2.79111 20 2.54222 19.8776 2.37333 19.6658Z"
      />
    </IconFrame>
  );
}

// KlassMate in-house SVG; source: mono/edusync-klassmate-mono-v1/packages/ui/src/icons/custom/UndoIcon.tsx. Cross-repo reuse approved by DECISIONS.md 2026-07-15 O9.
export function UndoIcon(): React.JSX.Element {
  return (
    <IconFrame viewBox="0 0 17 16">
      <path
        fill="currentColor"
        d="M15.25 14.5C15.25 14.9142 15.5858 15.25 16 15.25C16.4142 15.25 16.75 14.9142 16.75 14.5H15.25ZM2 6.13639L1.45452 5.62166L0.968805 6.13639L1.45452 6.65112L2 6.13639ZM2 6.9045H11.5789V5.4045H2V6.9045ZM15.25 10.8397V14.5H16.75V10.8397H15.25ZM11.5789 6.9045C13.566 6.9045 15.25 8.62475 15.25 10.8397H16.75C16.75 7.87952 14.4753 5.4045 11.5789 5.4045V6.9045ZM5.82952 0.985269L1.45452 5.62166L2.54548 6.65112L6.92048 2.01473L5.82952 0.985269ZM1.45452 6.65112L5.82952 11.2875L6.92048 10.258L2.54548 5.62166L1.45452 6.65112Z"
      />
    </IconFrame>
  );
}

// KlassMate in-house SVG; source: mono/edusync-klassmate-mono-v1/packages/ui/src/icons/custom/RedoIcon.tsx. Cross-repo reuse approved by DECISIONS.md 2026-07-15 O9.
export function RedoIcon(): React.JSX.Element {
  return (
    <IconFrame viewBox="0 0 17 16">
      <path
        fill="currentColor"
        d="M2.41797 14.5C2.41797 14.9142 2.08218 15.25 1.66797 15.25C1.25375 15.25 0.917968 14.9142 0.917968 14.5H2.41797ZM15.668 6.13639L16.2135 5.62166L16.6992 6.13639L16.2135 6.65112L15.668 6.13639ZM15.668 6.9045H6.08902V5.4045H15.668V6.9045ZM2.41797 10.8397V14.5H0.917968V10.8397H2.41797ZM6.08902 6.9045C4.10196 6.9045 2.41797 8.62475 2.41797 10.8397H0.917968C0.917968 7.87952 3.19272 5.4045 6.08902 5.4045V6.9045ZM11.8385 0.985269L16.2135 5.62166L15.1225 6.65112L10.7475 2.01473L11.8385 0.985269ZM16.2135 6.65112L11.8385 11.2875L10.7475 10.258L15.1225 5.62166L16.2135 6.65112Z"
      />
    </IconFrame>
  );
}

// KlassMate in-house SVG; source: mono/edusync-klassmate-mono-v1/packages/ui/src/icons/custom/v1/V1ClassroomChevronDownIcon.tsx. Cross-repo reuse approved by DECISIONS.md 2026-07-15 O9.
export function ChevronDownIcon(): React.JSX.Element {
  return (
    <IconFrame viewBox="0 0 17 16">
      <path
        fill="currentColor"
        d="M8.5 10.9998L3.5 5.9998L4.2 5.2998L8.5 9.59981L12.8 5.2998L13.5 5.9998L8.5 10.9998Z"
      />
    </IconFrame>
  );
}

// KlassMate in-house SVG; source: mono/edusync-klassmate-mono-v1/packages/ui/src/icons/custom/v1/V1WhiteboardLockIcon.tsx. Cross-repo reuse approved by DECISIONS.md 2026-07-15 O9.
export function LockIcon(): React.JSX.Element {
  return (
    <IconFrame viewBox="0 0 18 18">
      <path
        fill="currentColor"
        d="M13.3209 7.91964H12.2405V4.67857C12.2405 3.81899 11.899 2.99461 11.2912 2.38679C10.6834 1.77897 9.85903 1.4375 8.99944 1.4375C8.13986 1.4375 7.31548 1.77897 6.70766 2.38679C6.09984 2.99461 5.75837 3.81899 5.75837 4.67857V7.91964H4.67801C4.39148 7.91964 4.11669 8.03347 3.91409 8.23607C3.71148 8.43868 3.59766 8.71347 3.59766 9V15.4821C3.59766 15.7687 3.71148 16.0435 3.91409 16.2461C4.11669 16.4487 4.39148 16.5625 4.67801 16.5625H13.3209C13.6074 16.5625 13.8822 16.4487 14.0848 16.2461C14.2874 16.0435 14.4012 15.7687 14.4012 15.4821V9C14.4012 8.71347 14.2874 8.43868 14.0848 8.23607C13.8822 8.03347 13.6074 7.91964 13.3209 7.91964ZM6.83873 4.67857C6.83873 4.10551 7.06637 3.55593 7.47159 3.15072C7.8768 2.7455 8.42638 2.51786 8.99944 2.51786C9.5725 2.51786 10.1221 2.7455 10.5273 3.15072C10.9325 3.55593 11.1602 4.10551 11.1602 4.67857V7.91964H6.83873V4.67857ZM13.3209 15.4821H4.67801V9H13.3209V15.4821Z"
      />
    </IconFrame>
  );
}

// KlassMate in-house SVG; source: mono/edusync-klassmate-mono-v1/packages/ui/src/icons/custom/v1/V1ClassroomStatusDotIcon.tsx. Cross-repo reuse approved by DECISIONS.md 2026-07-15 O9.
export function StatusDotIcon(): React.JSX.Element {
  return (
    <IconFrame viewBox="0 0 8 9">
      <circle cx="4" cy="4.5" r="4" fill="currentColor" />
    </IconFrame>
  );
}
